import React, { useCallback, useEffect, useState } from 'react'
import { getFeedTrash, retryFeedTrash, FeedTrashRecordRow } from './core/_requests'

// Feed-mode timeline deletes for one client. Feed mode publishes on the
// profile's own timeline and FB copies the post into the groups; the bot then
// deletes the timeline original. A delete the bot gave up on is listed here so
// it can be re-queued — the profile's bot retries it within ~45s if it's running.
// Records with no post id (FB never answered the publish) can't be retried;
// those posts have to be deleted by hand.

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  failed: { text: 'נכשל', color: '#f1416c' },
  pending: { text: 'בתור', color: '#ffc700' },
  unknown_id: { text: 'אין מזהה פוסט', color: '#7e8299' },
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '')

const FeedTrashPanel: React.FC<{ accountId: string }> = ({ accountId }) => {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [records, setRecords] = useState<FeedTrashRecordRow[]>([])
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await getFeedTrash(accountId)
      setCounts(res.data.counts || {})
      setRecords(res.data.records || [])
    } catch (e) {
      console.error('Failed to load feed deletes:', e)
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => { load() }, [load])

  const retry = async (ids?: string[]) => {
    setBusy(true)
    try {
      const res = await retryFeedTrash(accountId, ids)
      await load()
      if (!ids) alert(`${res.data.requeued} מחיקות הוחזרו לתור`)
    } catch (e: any) {
      alert('שגיאה בניסיון חוזר: ' + (e?.response?.data?.error || e?.message || 'Unknown error'))
    } finally {
      setBusy(false)
    }
  }

  const total = (counts.done || 0) + (counts.failed || 0) + (counts.pending || 0) + (counts.unknown_id || 0)
  if (loading || total === 0) return null
  const failed = counts.failed || 0
  const unknown = counts.unknown_id || 0
  const bad = failed + unknown

  return (
    <div style={{ border: `1px solid ${bad ? '#f1416c55' : '#e5e7eb'}`, borderRadius: 10, padding: 14, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e1e2d' }}>
            <i className="bi bi-trash3" style={{ marginLeft: 6, color: bad ? '#f1416c' : '#2b70fa' }}></i>
            מחיקת פוסטים מהפיד (30 יום)
            <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ marginRight: 6, fontSize: 11, color: '#9ca3af' }}></i>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            נמחקו {counts.done || 0} · נכשלו <b style={{ color: failed ? '#f1416c' : undefined }}>{failed}</b>
            {' '}· בתור {counts.pending || 0}
            {unknown ? <> · ללא מזהה (למחוק ידנית) <b style={{ color: '#f1416c' }}>{unknown}</b></> : null}
          </div>
        </div>
        {failed > 0 && (
          <button
            disabled={busy}
            onClick={() => retry()}
            style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#2b70fa', color: '#fff', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
          >
            <i className="bi bi-arrow-repeat" style={{ marginLeft: 4 }}></i>
            נסה שוב את כל הכושלים ({failed})
          </button>
        )}
      </div>

      {open && records.length > 0 && (
        <div style={{ marginTop: 12, maxHeight: 360, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#9ca3af', textAlign: 'right' }}>
                <th style={{ padding: 6 }}>פוסט</th>
                <th style={{ padding: 6 }}>פרופיל</th>
                <th style={{ padding: 6 }}>סטטוס</th>
                <th style={{ padding: 6 }}>שגיאה</th>
                <th style={{ padding: 6 }}>נוצר</th>
                <th style={{ padding: 6 }}></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => {
                const st = STATUS_LABEL[r.status] || { text: r.status, color: '#7e8299' }
                return (
                  <tr key={r._id} style={{ borderTop: '1px solid #f1f1f4' }}>
                    <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                      {r.post_url ? <a href={r.post_url} target="_blank" rel="noreferrer">{r.post_id || 'קישור'}</a> : '—'}
                    </td>
                    <td style={{ padding: 6 }}>{r.profile_name}</td>
                    <td style={{ padding: 6, color: st.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {st.text}{r.attempts ? ` (${r.attempts})` : ''}{r.retry_count ? ` · ${r.retry_count} ניסיונות חוזרים` : ''}
                    </td>
                    <td style={{ padding: 6, color: '#7e8299', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.last_error}>
                      {r.last_error}
                    </td>
                    <td style={{ padding: 6, whiteSpace: 'nowrap', color: '#7e8299' }}>{fmt(r.created_at)}</td>
                    <td style={{ padding: 6 }}>
                      {r.status === 'failed' && r.post_id && (
                        <button
                          disabled={busy}
                          onClick={() => retry([r._id])}
                          style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #2b70fa', background: '#fff', color: '#2b70fa', fontSize: 11, cursor: busy ? 'not-allowed' : 'pointer' }}
                        >
                          נסה שוב
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default FeedTrashPanel
